import { CenterContainer } from 'components/common/CenterContainer'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import Footer from 'components/Footer'
import { NextSeo } from 'next-seo'
import EffectiveNav from 'components/Nav/EffectiveNav'
import Typography from 'components/common/Typography'
import HelpFAQ from 'components/discover/DiscoverEvents/HelpFAQ'
import { useWindowDimensions } from 'hooks'
import tailwind from 'lib/tailwind'
import ModalTrigger from 'components/common/ModalTrigger'
import VideoEmbedModal from 'components/VideoEmbedModal/VideoEmbedModal'
import moment from 'moment'
import { motion } from 'framer-motion'
import { NoSSRCarousel } from 'components/common/NoSSRPageScroll'
import TrackableClick from 'components/common/TrackableClick'
import MFDAnnouncementBanner from 'components/Collectives/Common/MFDAnnouncementBanner'

const metaImage =
  'https://i.upstreamapp.com/ogImages/collectives/nft-dao-open-graph.png'
const metaImageAlt = `The DAO for NFT Projects`
const metaUrl = 'https://upstreamapp.com/nft-dao'

const WhyLaunchContent = [
  {
    image: '/static/nftDao/community.svg',
    imageSize: { width: 98, height: 131 },
    title: 'Engage Your Community of NFT Holders'
  },
  {
    image: '/static/nftDao/pass.svg',
    imageSize: { width: 128, height: 131 },
    title: 'Your NFT Collections are the “Entry Pass” to DAO '
  },
  {
    image: '/static/nftDao/treasury.svg',
    imageSize: { width: 91, height: 131 },
    title: 'Utilize Your DAO Treasury for Your Community Members'
  }
]

const FeatureGridContent = [
  {
    image: '/static/investmentDao/lock.svg',
    imageSize: { width: 64, height: 88 },
    title: 'DAO Vault',
    description:
      'Upstream uses Gnosis multisig wallets for industry-leading security.'
  },
  {
    image: '/static/investmentDao/governance.svg',
    imageSize: { width: 77, height: 88 },
    title: 'Governance',
    description: `Members receive governance tokens proportional to their contribution to vote on proposals.
      `
  },
  {
    image: '/static/investmentDao/voting.svg',
    imageSize: { width: 89, height: 88 },
    title: 'Voting',
    description: `Vote on proposals to make decisions. Once a proposal passes, it's executed on-chain by designated signators.`
  },
  {
    image: '/static/investmentDao/polls.svg',
    imageSize: { width: 68, height: 86 },
    title: 'Polls',
    description: `Take a quick pulse of community sentiment with polls.`
  },
  {
    image: '/static/investmentDao/chat.svg',
    imageSize: { width: 82, height: 82 },
    title: 'Chat',
    description: `Stay connected to fellow DAO members and participate in discussions.`
  },
  {
    image: '/static/investmentDao/events.svg',
    imageSize: { width: 82, height: 80 },
    title: 'Events',
    description: `Host your DAO Town Halls and weekly meetings on Upstream’s in-platform event function.`
  },
  {
    image: '/static/investmentDao/legal.svg',
    imageSize: { width: 87, height: 86 },
    title: 'Legal Support',
    description: `Get your questions answered by independent legal counsel.`
  }
]

const PeopleSayingContent = [
  {
    text: (
      <>
        If you own an OG ToastPunk, please make sure you are signed up to the
        <Typography color="blue" component="span" size="inherit">
          {' '}
          @ToastPunk OG DAO
        </Typography>
        . All collection royalties will be transferred in today for use by the
        DAO and all DAO members will also receive a free gift airdrop today.
        Airdrop starts in 12 hrs.{' '}
        <Typography color="blue" component="span" size="inherit">
          https://toastpunk.upstreamapp.com
        </Typography>
      </>
    ),
    image: '/static/nftDao/ToastPunk.png',
    imageSize: { width: 140, height: 140 },
    title: `ToastPunk`,
    userRef: '@toastpunk',
    link: `https://twitter.com/toastpunk/status/1554271357080584192?s=20&t=C5yfCVwQlJyDcczTDqtVmA`
  },
  {
    text: (
      <>
        We are starting a new moment in the CryptoRastas community. Today we
        launch the CryptoRastas DAO using{' '}
        <Typography color="blue" component="span" size="inherit">
          @joinupstream
        </Typography>{' '}
        So if you own a CR NFT just join{' '}
        <Typography color="blue" component="span" size="inherit">
          cryptorastas.upstreamapp.com
        </Typography>
      </>
    ),
    image: '/static/nftDao/CryptoRastas.png',
    imageSize: { width: 140, height: 140 },
    title: 'CryptoRastas',
    userRef: '@cryptorastas',
    link: `https://twitter.com/cryptorastas/status/1545514389465432067?s=20&t=C5yfCVwQlJyDcczTDqtVmA`
  },
  {
    text: (
      <>
        Finally in the Illuminati DAO, thanks to this un-claimed
        <Typography color="blue" component="span" size="inherit">
          {' '}
          @secretsociety NFT
        </Typography>
        . Looks like
        <Typography color="blue" component="span" size="inherit">
          {' '}
          @truth
        </Typography>{' '}
        has BIG things coming in the near future. Looking forward to being apart
        of this community as they execute.
        <Typography color="blue" component="span" size="inherit">
          {' '}
          @ajt @process_grey
        </Typography>
      </>
    ),
    image: '/static/nftDao/Illuminati.png',
    imageSize: { width: 140, height: 140 },
    title: 'Illuminati NFT',
    userRef: '@kobiyashi_eth',
    link: `https://twitter.com/kobiyashi_eth/status/1551254811055599617?s=20&t=8ZsTR_A8I6un5qUoCrfX0g`
  }
]

function CollectivesPage() {
  const whiteBoxStyle = {
    boxShadow: '0px 4px 45px rgb(0,0,0, 0.04)',
    borderRadius: '10px'
  }

  const screen = useWindowDimensions()
  const xlBreakpoint = Number(tailwind.theme?.screens?.['xl'].replace('px', ''))
  const smBreakpoint = Number(tailwind.theme?.screens?.['sm'].replace('px', ''))

  const isLarge = (screen.width || 1000) < xlBreakpoint
  const isXSmall = (screen.width || 1000) < smBreakpoint

  const [itemWidthInPx, setItemWidthInPx] = useState(590)

  useEffect(() => {
    if (!screen.width) {
      return
    }
    setItemWidthInPx(
      isXSmall ? screen.width * 0.8 : isLarge ? 500 : 590 //screen.width * 0.333 - 40
    )
  }, [isLarge, isXSmall, screen.width])

  return (
    <>
      <NextSeo
        openGraph={{
          url: metaUrl,
          images: [
            {
              url: metaImage,
              width: 1200,
              height: 630,
              alt: metaImageAlt
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image'
        }}
        additionalMetaTags={[
          {
            name: 'twitter:image',
            content: metaImage
          },
          {
            name: 'twitter:image:alt',
            content: 'DAOs Introduction Flyer'
          }
        ]}
      />

      <MFDAnnouncementBanner bannerStyle="grey" />

      <EffectiveNav />

      <div className="w-full -mt-8">
        <CenterContainer>
          <div className="px-8 xl:px-0 pt-16 flex flex-1 flex-col items-center md:items-start md:justify-between md:flex-row-reverse max-w-6xl w-full">
            <div className="md:ml-28 lg:ml-60 mt-8">
              <Image
                src="/static/nftDao/header.svg"
                width={454}
                height={355}
                alt="Upstream DAO Heros"
              />
            </div>

            <div className="sm:w-[30rem] mt-12 sm:mt-10 text-center sm:text-left">
              <Typography
                className="flex sm:hidden text-5xl"
                fontWeight="black"
              >
                DAOs for NFT Projects
              </Typography>

              <Typography
                className="hidden sm:flex text-7xl"
                fontWeight="black"
              >
                DAOs for NFT Projects
              </Typography>

              <div className="mt-4">
                <h1 className="font-nunito text-[22px]">
                  Build community around your NFT project with a token-gated DAO
                </h1>
                <br />
              </div>
            </div>
          </div>
        </CenterContainer>
      </div>

      <CenterContainer className="px-8 mt-20 -mb-64">
        <div
          style={whiteBoxStyle}
          className="bg-white border px-20 py-20 w-full max-w-6xl"
        >
          <div className="flex justify-center mb-16 text-center">
            <Typography className="text-3xl" fontWeight="bold">
              Why Launch an Upstream DAO for Your NFT Project?
            </Typography>
          </div>

          <div className="flex flex-col md:flex-row space-x-6 space-y-16 md:space-y-0">
            {WhyLaunchContent.map(item => (
              <div
                className="flex-1 flex flex-col items-center"
                key={item.title}
              >
                <div className="flex-1">
                  <Image
                    alt={item.title}
                    src={item.image}
                    width={item.imageSize.width}
                    height={item.imageSize.height}
                  />
                </div>
                <div className="flex-1">
                  <div
                    style={{ maxWidth: '18rem' }}
                    className="flex mt-10 flex-col space-y-2 items-center text-center"
                  >
                    <Typography size="xl">{item.title}</Typography>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CenterContainer>

      <div className="landing-page-gradient-box ">
        <div className="flex flex-col items-center px-8 w-full mt-64 pt-8">
          <Typography className="text-3xl" fontWeight="bold">
            Key DAO Features
          </Typography>

          <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 mt-20 w-full max-w-6xl">
            {FeatureGridContent.map(content => (
              <div
                className="lg:aspect-w-10 lg:aspect-h-8 w-full"
                key={content.title}
              >
                <div className="bg-white border-[1px] border-solid border-[#e5e7eb] p-6 flex flex-col shadow-[0px_4px_45px_rgb(0,0,0,0.04)] hover:shadow-[0px_4px_45px rgb(0,0,0,0.1)] transition-all duration-200 ease-out">
                  <div className="flex-1">
                    <Image
                      alt={content.title}
                      src={content.image}
                      width={content.imageSize.width}
                      height={content.imageSize.height}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col space-y-2">
                      <Typography fontWeight="bold" size="heading">
                        {content.title}
                      </Typography>
                      <Typography
                        fontWeight="light"
                        className="text-sm md:text-md xl:text-lg mt-2"
                      >
                        {content.description}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="sm:hidden mt-8 w-full">
            <NoSSRCarousel
              itemWidth={320}
              items={FeatureGridContent.map(content => (
                <div
                  className="w-full bg-white shadow-lg rounded-lg p-4 h-full"
                  key={content.title}
                >
                  <div className="p-4 flex flex-col">
                    <div className="flex-1">
                      <Image
                        alt={content.title}
                        src={content.image}
                        width={content.imageSize.width}
                        height={content.imageSize.height}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col space-y-2">
                        <Typography className="mt-4" size="heading">
                          {content.title}
                        </Typography>
                        <Typography className="text-md md:text-md lg:text-lg">
                          {content.description}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#68A2FF] to-[#F0A6FD] p-10 overflow-x-hidden">
        <div className="flex flex-row items-center space-x-8 pb-10 pt-4">
          <Typography className="text-3xl" fontWeight="bold" color="white">
            Successful NFT Project DAOs on Upstream
          </Typography>
        </div>

        <NoSSRCarousel
          itemWidth={itemWidthInPx}
          items={PeopleSayingContent.map((item, i) => (
            <a
              className="bg-white rounded-lg px-16 py-10 shrink-0 flex flex-col h-full justify-between hover:shadow-xl"
              href={item.link}
              key={i}
              target="_blank"
              rel="noreferrer"
            >
              <div className="pb-10 hidden sm:block">
                <Typography size="lg" component={'div'}>
                  {item.text}
                </Typography>
              </div>

              <div className="pb-4 sm:hidden">
                <Typography size="md" component={'div'}>
                  {item.text}
                </Typography>
              </div>

              <div className="flex flex-row items-center space-x-4">
                <Image
                  alt={item.title}
                  src={item.image}
                  width={item.imageSize.width}
                  height={item.imageSize.height}
                />

                <div className="flex flex-col">
                  <Typography size="heading">{item.title}</Typography>
                  <Typography color="gray">{item.userRef}</Typography>
                </div>
              </div>
            </a>
          ))}
        />
        {/* )} */}
      </div>

      <CenterContainer className="bg-gradient-to-r from-[#E7EDFD] via-[#E7EDFD] to-[#F8F1FE] px-10">
        <div className="overflow-hidden pt-12 mt-4 max-w-6xl">
          <div className="flex flex-col lg:flex-row lg:items-end">
            <div
              style={{ lineHeight: '64px' }}
              className="flex flex-col flex-[1_0_0%] space-y-4 pr-8 pl-4 mb-12"
            >
              <Typography className="text-[4rem] pb-4" fontWeight="black">
                Explore Upstream
              </Typography>
              <Typography
                className="pb-5 max-w-[347px]"
                size="xl"
                fontWeight="light"
              >
                See how a DAO makes it easy to engage and energize your
                community of NFT holders.
              </Typography>
            </div>

            <div className="flex-[2_0_0%] mt-8 lg:mt-0">
              <motion.div
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="lg:min-w-[600px] xl:min-w-[700px] relative group cursor-pointer">
                  <Image
                    alt="Demo screen shot"
                    src="/static/events/demo_screen_shot.png"
                    width={802}
                    height={445}
                    layout="responsive"
                  />

                  <ModalTrigger
                    trigger={onClick => (
                      <TrackableClick event="NFT-DAO Page: video opened">
                        <div
                          className="bg-no-repeat bg-center bg-[length:92px_92px] inset-0 opacity-80 group-hover:opacity-100 absolute cursor-pointer"
                          style={{
                            backgroundImage:
                              'url(/static/investmentDao/play-button.png)'
                          }}
                          onClick={onClick}
                        />
                      </TrackableClick>
                    )}
                  >
                    <VideoEmbedModal
                      title={`Upstream Tutorial`}
                      postedTimestamp={moment('2022-04-06').toDate()}
                      url={`https://www.youtube.com/embed/Q_XyIrZvSq8`}
                      width={800}
                    />
                  </ModalTrigger>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </CenterContainer>

      <CenterContainer>
        <div className="px-10 mt-16 w-full md:w-4/5 xl:w-3/5">
          <Typography size="default" fontWeight="light">
            Disclaimer: The information provided by Upstream does not constitute
            legal advice, nor is it intended to take the place of the advice of
            independent legal counsel.
          </Typography>
        </div>
      </CenterContainer>

      <HelpFAQ />
      <Footer />
    </>
  )
}

export default CollectivesPage
